import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, Award, Clock, ChevronRight, Lock, CheckCircle, 
  Play, LogOut, User, ArrowLeft, BarChart3, Shield
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { getPlChapters, getPlProgress } from '@/services/api';
import { toast } from 'sonner';

const PreLicensingDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [chaptersRes, progressRes] = await Promise.all([
        getPlChapters(),
        getPlProgress()
      ]);
      setChapters(chaptersRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusIcon = (status, isLocked) => {
    if (isLocked) return <Lock className="w-5 h-5" />;
    if (status === 'completed') return <CheckCircle className="w-5 h-5" />;
    return <Play className="w-5 h-5" />;
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'text-green-500';
    if (status === 'in_progress') return 'text-amber-500';
    return 'text-muted-foreground';
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  const completedCount = progress?.completed_chapters || 0;
  const totalChapters = progress?.total_chapters || chapters.length;
  const pct = progress?.percentage || 0;

  return (
    <div className="min-h-screen bg-background flex" data-testid="pl-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border h-screen fixed left-0 top-0 flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8" />
            <span className="font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              COLORADO BEA
            </span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Courses
          </Link>

          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-4">
            Pre-Licensing Chapters
          </div>

          {chapters.map((ch) => (
            <button
              key={ch.id}
              data-testid={`pl-sidebar-chapter-${ch.chapter_number}`}
              onClick={() => !ch.is_locked && navigate(`/pre-licensing/chapter/${ch.id}`)}
              disabled={ch.is_locked}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                ch.is_locked
                  ? 'text-muted-foreground/50 cursor-not-allowed'
                  : ch.status === 'completed'
                  ? 'text-green-500 hover:bg-muted'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              {getStatusIcon(ch.status, ch.is_locked)}
              <span className="truncate">Ch {ch.chapter_number}: {ch.title}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-border space-y-2">
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
            <User className="w-4 h-4" />
            <span className="truncate">{user?.first_name} {user?.last_name}</span>
          </div>
          {isAdmin && (
            <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors">
              <Shield className="w-4 h-4" /> Admin Portal
            </Link>
          )}
          <button
            onClick={handleLogout}
            data-testid="pl-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 rounded-lg hover:bg-muted transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Bail Bond Pre-Licensing Course
          </h1>
          <p className="text-muted-foreground mt-1">Colorado Bail Bonds Pre-Licensing Education — 25 Credit Hours</p>
        </div>

        {/* Progress Card */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8" data-testid="pl-progress-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="font-semibold">Course Progress</h2>
                <p className="text-sm text-muted-foreground">
                  {completedCount} of {totalChapters} chapters completed
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold">{pct}%</span>
              <p className="text-xs text-muted-foreground">
                {formatTime(progress?.total_time_spent_seconds || 0)} studied
              </p>
            </div>
          </div>
          <Progress value={pct} className="h-2" />
        </div>

        {/* Chapters Grid */}
        <div className="space-y-3" data-testid="pl-chapters-list">
          {chapters.map((ch) => (
            <div
              key={ch.id}
              data-testid={`pl-chapter-card-${ch.chapter_number}`}
              className={`bg-card border rounded-xl p-5 transition-all ${
                ch.is_locked
                  ? 'border-border/50 opacity-60'
                  : ch.status === 'completed'
                  ? 'border-green-500/30 hover:border-green-500/50'
                  : 'border-border hover:border-amber-500/50 cursor-pointer'
              }`}
              onClick={() => !ch.is_locked && navigate(`/pre-licensing/chapter/${ch.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    ch.status === 'completed'
                      ? 'bg-green-500/10 text-green-500'
                      : ch.is_locked
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    {getStatusIcon(ch.status, ch.is_locked)}
                  </div>
                  <div>
                    <h3 className="font-semibold">
                      Chapter {ch.chapter_number}: {ch.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{ch.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {ch.credit_hours} hrs
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" /> {ch.total_sections} sections
                      </span>
                      {ch.time_spent_seconds > 0 && (
                        <span>{formatTime(ch.time_spent_seconds)} studied</span>
                      )}
                    </div>
                  </div>
                </div>
                {!ch.is_locked && (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Final Exam Section */}
        <div className="mt-8 bg-card border border-border rounded-xl p-6" data-testid="pl-final-exam-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                progress?.exam_passed
                  ? 'bg-green-500/10 text-green-500'
                  : completedCount >= totalChapters
                    ? 'bg-amber-500/10 text-amber-500'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {progress?.exam_passed ? <CheckCircle className="w-6 h-6" /> : <Award className="w-6 h-6" />}
              </div>
              <div>
                <h3 className="text-lg font-bold">Pre-Licensing Final Examination</h3>
                {progress?.exam_passed ? (
                  <>
                    <p className="text-sm text-green-500 font-semibold mt-0.5">
                      Passed — {progress.exam_correct}/{progress.exam_total} ({progress.exam_score}%)
                    </p>
                    {progress.certificate_number && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Certificate {progress.certificate_number} — {progress.certificate_status === 'approved' ? 'Approved' : 'Pending Review'}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      150 Questions &bull; 3 Hour Time Limit &bull; 75% Passing Score &bull; Proctor Required
                    </p>
                    {completedCount < totalChapters && (
                      <p className="text-xs text-amber-500 mt-1">
                        Complete all {totalChapters} chapters to unlock the final exam
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            {progress?.exam_passed ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="font-bold text-green-500">PASSED</span>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/pre-licensing/final-exam')}
                disabled={completedCount < totalChapters}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="pl-take-final-exam-btn"
              >
                {completedCount >= totalChapters ? 'Take Final Exam' : (
                  <><Lock className="w-4 h-4 mr-2" /> Locked</>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PreLicensingDashboardPage;
